import {
  faFile,
  faSpinnerThird,
  faTriangleExclamation,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons'
import { Icon, Button, Text } from '@/ui'
import { mtc, TextColors, TypographyStyles } from '@/styles'
import type { AttachmentItem } from '../lib/attachments.types'
import { formatFileSize } from '../lib/formatFileSize'

interface AttachmentRowProps {
  item: AttachmentItem
  onRetry: (id: string) => void
  onCancel: (id: string) => void
  onRemove: (id: string) => void
  disabled?: boolean
}

const STATUS_ICON = {
  uploading: faSpinnerThird,
  uploaded: faFile,
  failed: faTriangleExclamation,
  rejected: faTriangleExclamation,
} as const

const failureMessage = (percent: number, error: string | null): string => {
  const reason = error
    ? `${error.charAt(0).toLowerCase()}${error.slice(1)}`
    : ''
  const reasonPart = reason ? ` ${reason}` : ''
  return `Upload failed at ${percent}%:${reasonPart} The file is still selected.`
}

export const AttachmentRow = ({
  item,
  onRetry,
  onCancel,
  onRemove,
  disabled = false,
}: AttachmentRowProps) => {
  const { id, fileName, size, status, percent, error } = item
  const isError = status === 'failed' || status === 'rejected'

  return (
    <div
      className={mtc(
        'flex items-center gap-3 rounded-[8px] border px-3 py-2',
        isError ? 'border-red-200 bg-red-25' : 'border-neutral-200 bg-neutral-0'
      )}
    >
      <Icon
        icon={STATUS_ICON[status]}
        className={mtc(status === 'uploading' && 'animate-spin')}
        color={isError ? 'text-red-400' : 'text-neutral-500'}
      />

      <div className="flex-1 min-w-0 flex flex-col gap-1">
        <Text
          variant={TypographyStyles.LABEL_SM}
          color={TextColors.HEADER}
          className="truncate"
        >
          {fileName}
        </Text>

        {status === 'uploading' && (
          <>
            <div className="h-1 w-full rounded-full bg-neutral-200 overflow-hidden">
              <div
                className="h-full rounded-full bg-moss-200"
                style={{ width: `${percent}%` }}
              />
            </div>
            <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
              Uploading · {percent}%
            </Text>
          </>
        )}

        {status === 'uploaded' && (
          <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
            {formatFileSize(size)}
          </Text>
        )}

        {status === 'failed' && (
          <Text variant={TypographyStyles.BODY_XS} color={TextColors.DANGER}>
            {failureMessage(percent, error)}
          </Text>
        )}

        {status === 'rejected' && (
          <Text variant={TypographyStyles.BODY_XS} color={TextColors.DANGER}>
            {error}
          </Text>
        )}
      </div>

      {status === 'uploading' && (
        <Button
          variant="ghost"
          size="xs"
          aria-label={`Cancel upload of ${fileName}`}
          onClick={() => onCancel(id)}
          disabled={disabled}
        >
          <Icon icon={faXmark} />
        </Button>
      )}

      {status === 'failed' && (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="xs"
            aria-label={`Retry upload of ${fileName}`}
            onClick={() => onRetry(id)}
            disabled={disabled}
          >
            Retry
          </Button>
          <Button
            variant="ghost"
            size="xs"
            aria-label={`Remove ${fileName}`}
            onClick={() => onRemove(id)}
            disabled={disabled}
          >
            <Icon icon={faXmark} />
          </Button>
        </div>
      )}

      {(status === 'uploaded' || status === 'rejected') && (
        <Button
          variant="ghost"
          size="xs"
          aria-label={`Remove ${fileName}`}
          onClick={() => onRemove(id)}
          disabled={disabled}
        >
          <Icon icon={faXmark} />
        </Button>
      )}
    </div>
  )
}
